if (!customElements.get('tab-list')) {
  customElements.define(
    'tab-list',
    class TabList extends HTMLUListElement {
      constructor() {
        super();

        this.controls.forEach((button) => button.addEventListener('click', this.handleButtonClick.bind(this)));
      }

      get controls() {
        return this._controls = this._controls || Array.from(this.querySelectorAll('[aria-controls]'));
      }

      handleButtonClick(event) {
        event.preventDefault();

        this.controls.forEach((button) => {
          button.setAttribute('aria-expanded', 'false');

          const panel = document.getElementById(button.getAttribute('aria-controls'));
          panel?.removeAttribute('open');
        });

        const target = event.currentTarget;
        target.setAttribute('aria-expanded', 'true');

        const panel = document.getElementById(target.getAttribute('aria-controls'));
        panel?.setAttribute('open', '');
      }

      reset() {
        const firstControl = this.controls[0];
        firstControl.dispatchEvent(new Event('click'));
      }
    }, { extends: 'ul' }
  );
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends DrawerElement {
      constructor() {
        super();

        this.onPrepareBundledSectionsListener = this.onPrepareBundledSections.bind(this);
        this.onCartRefreshListener = this.onCartRefresh.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get shouldAppendToBody() {
        return false;
      }

      get recentlyViewed() {
        return this.querySelector('recently-viewed');
      }

      get tabList() {
        return this.querySelector('[is="tab-list"]');
      }

      connectedCallback() {
        super.connectedCallback();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
        document.addEventListener('cart:refresh', this.onCartRefreshListener);
        if (this.recentlyViewed) {
          this.recentlyViewed.addEventListener('is-empty', this.onRecentlyViewedEmpty.bind(this));
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();
    
        document.removeEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
        document.removeEventListener('cart:refresh', this.onCartRefreshListener);
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }

      onRecentlyViewedEmpty() {
        this.recentlyViewed.innerHTML = `
        <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
          <div class="drawer__empty grid gap-5 md:gap-8">
            <h2 class="drawer__empty-text heading leading-none tracking-tight">${theme.strings.recentlyViewedEmpty}</h2>
          </div>
        </div>
        `;
      }

      async onCartRefresh(event) {
        const id = `MiniCart-${this.sectionId}`;
        if (document.getElementById(id) === null) return;

        const responseText = await (await fetch(`${theme.routes.root_url}?section_id=${this.sectionId}`)).text();
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;

        if (event.detail.open === true) {
          this.show();
        }
      }

      show(focusElement = null, animate = true) {
        super.show(focusElement, animate);

        if (this.tabList) {
          this.tabList.reset();

          if (this.open) {
            theme.a11y.trapFocus(this, this.focusElement);
          }
        }
      }
    }
  );
}

if (!customElements.get('cart-remove-button')) {
  customElements.define(
    'cart-remove-button',
    class CartRemoveButton extends HTMLAnchorElement {
      constructor() {
        super();

        this.addEventListener('click', (event) => {
          event.preventDefault();

          const cartItems = this.closest('cart-items');
          cartItems.updateQuantity(this.getAttribute('data-index'), 0);
        });
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('cart-items')) {
  customElements.define(
    'cart-items',
    class CartItems extends HTMLElement {
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      onChange(event) {
        this.validateQuantity(event);
      }

      onCartUpdate(event) {
        if (event.cart.errors) {
          this.onCartError(event.cart.errors, event.target);
          return;
        }

        const sectionToRender = new DOMParser().parseFromString(event.cart.sections[this.sectionId], 'text/html');

        const miniCart = document.querySelector(`#MiniCart-${this.sectionId}`);
        if (miniCart) {
          const updatedElement = sectionToRender.querySelector(`#MiniCart-${this.sectionId}`);
          if (updatedElement) {
            if (event.source === 'cart-discount') {
              const cartDiscount = updatedElement.querySelector(`#CartDiscount-${this.sectionId}`);
              if (cartDiscount) {
                cartDiscount.hidden = false
                cartDiscount.setAttribute('open', 'immediate');
                cartDiscount.setAttribute('active', '');
              }
            }
            miniCart.innerHTML = updatedElement.innerHTML;
          }
        }

        const mainCart = document.querySelector(`#MainCart-${this.sectionId}`);
        if (mainCart) {
          const updatedElement = sectionToRender.querySelector(`#MainCart-${this.sectionId}`);
          if (updatedElement) {
            if (event.source === 'cart-discount') {
              const cartDiscount = updatedElement.querySelector(`#CartDiscount-${this.sectionId}`);
              if (cartDiscount) {
                cartDiscount.hidden = false
                cartDiscount.setAttribute('open', '');
                cartDiscount.setAttribute('aria-expanded', 'true');
              }
            }
            
            const scrollTop = window.scrollY;
            mainCart.innerHTML = updatedElement.innerHTML;

            if (event.source === 'cart-discount') {
              requestIdleCallback(() => {
                window.scrollTo({
                  top: scrollTop,
                  behavior: 'instant'
                });
              });
              
            }
          }
          else {
            mainCart.closest('.cart').classList.add('is-empty');
            mainCart.remove();
          }
        }

        const lineItem = document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);
        if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
          theme.a11y.trapFocus(mainCart || miniCart, lineItem.querySelector(`[name="${event.name}"]`));
        }
        else if (event.cart.item_count === 0) {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('a'))
            : theme.a11y.trapFocus(document.querySelector('.empty-state'), document.querySelector('.empty-state__link'));
        }
        else {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('.horizontal-product__title'))
            : theme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart__item-title'));
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: event.cart
          }
        }));
      }

      onCartError(errors, target) {
        if (target) {
          // this.updateQuantity(target.getAttribute('data-index'), target.defaultValue, document.activeElement.getAttribute('name'), target);
          this.disableLoading(target.getAttribute('data-index'));
          this.setValidity(target, errors);
          return;
        }
        else {
          window.location.href = theme.routes.cart_url;
        }

        alert(errors);
      }

      updateQuantity(line, quantity, name, target) {
        this.enableLoading(line);

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        const body = JSON.stringify({
          id: line,
          quantity,
          sections: sectionsToBundle
        });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState, target, line, name });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      enableLoading(line) {
        this.classList.add('pointer-events-none');

        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = false;
      }

      disableLoading(line) {
        this.classList.remove('pointer-events-none');

        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = true;
      }

      setValidity(target, message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();
      }

      validateQuantity(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        let message = '';

        if (inputValue < parseInt(target.getAttribute('data-min'))) {
          message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
        }
        else if (inputValue > parseInt(target.max)) {
          message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
        }
        else if (inputValue % parseInt(target.step) !== 0) {
          message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
        }

        if (message) {
          this.setValidity(target, message);
        }
        else {
          target.setCustomValidity('');
          target.reportValidity();
          this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
        }
      }
    }
  );
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
      }

      onChange(event) {
        const body = JSON.stringify({ note: event.target.value });
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } });
      }
    }
  );
}

if (!customElements.get('main-cart')) {
  customElements.define(
    'main-cart',
    class MainCart extends HTMLElement {
      constructor() {
        super();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSections.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }
    }
  );
}

if (!customElements.get('country-province')) {
  customElements.define(
    'country-province',
    class CountryProvince extends HTMLElement {
      constructor() {
        super();

        this.provinceElement = this.querySelector('[name="address[province]"]');
        this.countryElement = this.querySelector('[name="address[country]"]');
        this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));

        if (this.getAttribute('country') !== '') {
          this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute('data-country')));
          this.countryElement.dispatchEvent(new Event('change'));
        }
        else {
          this.handleCountryChange();
        }
      }

      handleCountryChange() {
        const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute('data-provinces'));
        this.provinceElement.parentElement.hidden = provinces.length === 0;

        if (provinces.length === 0) {
          return;
        }

        this.provinceElement.innerHTML = '';

        provinces.forEach((data) => {
          const selected = data[1] === this.getAttribute('data-province');
          this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
        });
      }
    }
  );
}

if (!customElements.get('shipping-calculator')) {
  customElements.define(
    'shipping-calculator',
    class ShippingCalculator extends HTMLFormElement {
      constructor() {
        super();

        this.onSubmitHandler = this.onSubmit.bind(this);
      }

      connectedCallback() {
        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.submitButton.addEventListener('click', this.onSubmitHandler);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.submitButton.removeEventListener('click', this.onSubmitHandler);
      }

      onSubmit(event) {
        event.preventDefault();

        this.abortController?.abort();
        this.abortController = new AbortController();

        const zip = this.querySelector('[name="address[zip]"]').value,
          country = this.querySelector('[name="address[country]"]').value,
          province = this.querySelector('[name="address[province]"]').value;

        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          shipping_address: { zip, country, province }
        });
        let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

        // remove double `/` in case shop might have /en or language in URL
        sectionUrl = sectionUrl.replace('//', '/');

        fetch(sectionUrl, { ...theme.utils.fetchConfig('javascript'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.shipping_rates) {
              this.formatShippingRates(parsedState.shipping_rates);
            }
            else {
              this.formatError(parsedState);
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            this.resultsElement.hidden = false;
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      formatError(errors) {
        const shippingRatesList = Object.keys(errors).map((errorKey) => {
          return `<li>${errors[errorKey]}</li>`;
        });
        this.resultsElement.classList.remove('alert--success');
        this.resultsElement.classList.add('alert--error');
        this.resultsElement.lastElementChild.innerHTML = `
          <p>${theme.shippingCalculatorStrings.error}</p>
          <ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>
        `;
      }

      formatShippingRates(shippingRates) {
        const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
          return `<li>${presentment_name}: ${currency} ${price}</li>`;
        });
        if (shippingRates.length) {
          this.resultsElement.classList.remove('alert--error');
          this.resultsElement.classList.add('alert--success');
        }
        else {
          this.resultsElement.classList.remove('alert--success');
          this.resultsElement.classList.add('alert--error');
        }
        this.resultsElement.lastElementChild.innerHTML = `
          <p>${shippingRates.length === 0 ? theme.shippingCalculatorStrings.notFound : shippingRates.length === 1 ? theme.shippingCalculatorStrings.oneResult : theme.shippingCalculatorStrings.multipleResults}</p>
          ${shippingRatesList === '' ? '' : `<ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>`}
        `;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLFormElement {
      constructor() {
        super();

        this.onApplyDiscount = this.applyDiscount.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }
      
      connectedCallback() {
        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.submitButton.addEventListener('click', this.onApplyDiscount);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.submitButton.removeEventListener('click', this.onApplyDiscount);
      }

      applyDiscount(event) {
        event.preventDefault();

        const discountCode = this.querySelector('[name="discount"]');
        if (!(discountCode instanceof HTMLInputElement) || typeof this.getAttribute('data-section-id') !== 'string') return;

        this.abortController?.abort();
        this.abortController = new AbortController();

        const discountCodeValue = discountCode.value.trim();
        if (discountCodeValue === '') return;

        const existingDiscounts = this.existingDiscounts();
        if (existingDiscounts.includes(discountCodeValue)) return;

        this.setDiscountError('');
        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.sectionId]
        });
        
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (
              parsedState.discount_codes.find((discount) => {
                return discount.code === discountCodeValue && discount.applicable === false;
              })
            ) {
              discountCode.value = '';
              this.setDiscountError(theme.discountStrings.error);
              return;
            }

            const newHtml = parsedState.sections[this.sectionId];
            const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
            const section = parsedHtml.getElementById(`shopify-section-${this.sectionId}`);
            if (section) {
              const discountCodes = section?.querySelectorAll('button[is="discount-remove"]') || [];
              const codes = Array.from(discountCodes)
                .map((element) => (element instanceof HTMLButtonElement ? element.getAttribute('data-discount') : null))
                .filter(Boolean);

              if (
                codes.length === existingDiscounts.length &&
                codes.every((code) => existingDiscounts.includes(code)) &&
                parsedState.discount_codes.find((discount) => {
                  return discount.code === discountCodeValue && discount.applicable === true;
                })
              ) {
                discountCode.value = '';
                this.setDiscountError(theme.discountStrings.shippingError);
                return;
              }
            }

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      removeDiscount(event) {
        if ((event instanceof KeyboardEvent && event.key !== 'Enter') || !(event instanceof MouseEvent)) {
          return;
        }

        const discountCode = event.target.getAttribute('data-discount');
        if (!discountCode) return;

        const existingDiscounts = this.existingDiscounts();
        const index = existingDiscounts.indexOf(discountCode);
        if (index === -1) return;

        existingDiscounts.splice(index, 1);

        this.abortController?.abort();
        this.abortController = new AbortController();

        this.setDiscountError('');
        event.target.setAttribute('loading', '');

        const body = JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.sectionId]
        });
        
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            event.target.removeAttribute('loading');
          });
      }

      existingDiscounts() {
        const discountCodes = [];
        const discountPills = this.querySelectorAll('button[is="discount-remove"]');
        for (const pill of discountPills) {
          if (pill.hasAttribute('data-discount')) {
            discountCodes.push(pill.getAttribute('data-discount'));
          }
        }
        return discountCodes;
      }

      setDiscountError(error) {
        this.resultsElement.lastElementChild.textContent = error;
        this.resultsElement.hidden = error.length === 0;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('discount-remove')) {
  customElements.define(
    'discount-remove',
    class DiscountRemove extends MagnetButton {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick(event) {
        const form = this.closest('form[is="cart-discount"]') || document.querySelector('form[is="cart-discount"]');

        if (form) {
          event.preventDefault();
          form.removeDiscount(event);
        }
      }
    }, { extends: 'button' }
  );
}



// ============================================================================
// VARIANT EXCLUSIVITY WITH GIFT WRAP MANAGEMENT
// Add this at the END of your cart.js file (replace previous version)
// ============================================================================

(function() {
  'use strict';
  
  console.log('Variant Exclusivity: Loading...');
  
  // Configuration
  const MANAGED_PRODUCTS = ['black-book-of-power'];
  const JOURNAL_PDF_ID = 47190821699741;      // Printable PDF journal
  const JOURNAL_SOFTCOVER_ID = 47190822912157; // Premium Softcover journal
  
  let lastAddedVariantId = null;
  let processTimeout = null;
  
  // Initialize
  function init() {
    if (window.variantExclusivityInit) return;
    window.variantExclusivityInit = true;
    
    console.log('Variant Exclusivity: Initialized');
    
    // Intercept add to cart to capture what's being added
    interceptAddToCart();
    
    // Listen for the AJAX product added event
    document.addEventListener('ajaxProduct:added', function(e) {
      console.log('Product added:', e.detail);
      
      if (e.detail && e.detail.product) {
        // Extract variant ID from the product details
        const variantId = e.detail.product.variant_id || 
                         e.detail.product.selected_variant_id || 
                         e.detail.product.id;
        
        if (variantId) {
          lastAddedVariantId = variantId;
          console.log('Captured variant ID from event:', variantId);
        }
        
        // Process after a delay (cancel any pending processes)
        scheduleProcess();
      }
    });
    
    // Also listen for cart updates as backup
    document.addEventListener('cart:updated', function(e) {
      // Only process if we have a recently added variant
      if (lastAddedVariantId) {
        scheduleProcess();
      }
    });
    
    // Check cart on initial load for gift wrap mismatches
    setTimeout(checkGiftWrapAlignment, 1000);
  }
  
  // Intercept fetch to capture variant being added
  function interceptAddToCart() {
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const [url, config] = args;
      
      if (url && url.includes('/cart/add')) {
        // Try to extract variant ID from request
        if (config && config.body) {
          try {
            let variantId = null;
            
            // Handle JSON body
            if (typeof config.body === 'string') {
              const data = JSON.parse(config.body);
              variantId = data.id || (data.items && data.items[0] && data.items[0].id);
            }
            // Handle FormData
            else if (config.body instanceof FormData) {
              variantId = config.body.get('id');
            }
            
            if (variantId) {
              lastAddedVariantId = variantId;
              console.log('Captured variant ID from fetch:', variantId);
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
      }
      
      return originalFetch.apply(this, args);
    };
  }
  
  // Schedule processing (with debounce)
  function scheduleProcess() {
    // Cancel any pending process
    if (processTimeout) {
      clearTimeout(processTimeout);
    }
    
    // Schedule new process
    processTimeout = setTimeout(processCart, 500);
  }
  
  // Main processing function
  async function processCart() {
    if (!lastAddedVariantId) {
      console.log('No variant ID captured');
      return;
    }
    
    console.log('Processing cart for variant:', lastAddedVariantId);
    
    try {
      // Get current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Find the item that was just added
      let addedItem = null;
      let addedItemHandle = null;
      let addedItemVariantTitle = null;
      
      for (const item of cart.items) {
        if (item.variant_id == lastAddedVariantId || item.id == lastAddedVariantId) {
          addedItem = item;
          addedItemHandle = item.handle;
          addedItemVariantTitle = item.variant_title || item.title;
          break;
        }
      }
      
      if (!addedItem) {
        console.log('Added item not found in cart');
        lastAddedVariantId = null;
        return;
      }
      
      console.log('Found added item:', addedItemVariantTitle);
      
      // Check if this is a managed product
      const isManaged = MANAGED_PRODUCTS.some(p => 
        addedItemHandle && addedItemHandle.includes(p)
      );
      
      if (!isManaged) {
        console.log('Not a managed product');
        lastAddedVariantId = null;
        return;
      }
      
      // Determine which journal variant should be kept/removed
      const isHardcover = addedItemVariantTitle.toLowerCase().includes('hardcover');
      const correctJournalId = isHardcover ? JOURNAL_SOFTCOVER_ID : JOURNAL_PDF_ID;
      const wrongJournalId = isHardcover ? JOURNAL_PDF_ID : JOURNAL_SOFTCOVER_ID;
      
      console.log('Book variant:', addedItemVariantTitle);
      console.log('Correct journal variant ID:', correctJournalId);
      console.log('Wrong journal variant ID:', wrongJournalId);
      
      // Find items to remove
      const toRemove = [];
      
      for (const item of cart.items) {
        // Remove OTHER variants of the same book product
        if (item.handle === addedItemHandle && 
            item.variant_id != lastAddedVariantId && 
            item.id != lastAddedVariantId) {
          toRemove.push({
            key: item.key,
            title: item.variant_title || item.title,
            type: 'book'
          });
        }
        
        // Remove WRONG journal variants
        if (item.variant_id == wrongJournalId || item.id == wrongJournalId) {
          toRemove.push({
            key: item.key,
            title: item.variant_title || item.title,
            type: 'journal'
          });
        }
      }
      
      if (toRemove.length > 0) {
        console.log('Removing items:', toRemove);
        await removeVariants(toRemove, addedItemVariantTitle);
      } else {
        console.log('No items to remove');
      }
      
    } catch (error) {
      console.error('Error processing cart:', error);
    } finally {
      // Reset for next add
      lastAddedVariantId = null;
      processTimeout = null;
    }
  }
  
  // Check gift wrap alignment on page load
  async function checkGiftWrapAlignment() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Check what book variant we have
      let hasHardcover = false;
      let hasEbookOrAudiobook = false;
      
      for (const item of cart.items) {
        const handle = item.handle;
        const isManaged = MANAGED_PRODUCTS.some(p => handle && handle.includes(p));
        
        if (isManaged) {
          const variantTitle = (item.variant_title || item.title).toLowerCase();
          if (variantTitle.includes('hardcover')) {
            hasHardcover = true;
          } else if (variantTitle.includes('ebook') || variantTitle.includes('audiobook')) {
            hasEbookOrAudiobook = true;
          }
        }
      }
      
      // If no book variants, exit
      if (!hasHardcover && !hasEbookOrAudiobook) return;
      
      // Determine which journal should be removed
      const toRemove = [];
      
      for (const item of cart.items) {
        // If we have Hardcover, remove PDF journals
        if (hasHardcover && (item.variant_id == JOURNAL_PDF_ID || item.id == JOURNAL_PDF_ID)) {
          toRemove.push({
            key: item.key,
            title: item.variant_title || item.title,
            type: 'journal'
          });
        }
        // If we have Ebook/Audiobook, remove Softcover journals
        else if (hasEbookOrAudiobook && (item.variant_id == JOURNAL_SOFTCOVER_ID || item.id == JOURNAL_SOFTCOVER_ID)) {
          toRemove.push({
            key: item.key,
            title: item.variant_title || item.title,
            type: 'journal'
          });
        }
      }
      
      if (toRemove.length > 0) {
        console.log('Found misaligned gift wraps on load, removing:', toRemove);
        await removeVariants(toRemove, null, true);
      }
      
    } catch (error) {
      console.error('Error checking gift wrap alignment:', error);
    }
  }
  
  // Remove variants from cart
  async function removeVariants(items, keepingTitle, silent = false) {
    const updates = {};
    items.forEach(item => {
      updates[item.key] = 0;
    });
    
    console.log('Removing items:', updates);
    
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      
      const result = await response.json();
      console.log('Items removed successfully');
      
      // Update cart display
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: false }
        }));
      }, 100);
      
      // Show notification (unless silent)
      if (!silent && keepingTitle) {
        const bookRemoved = items.some(i => i.type === 'book');
        const journalRemoved = items.some(i => i.type === 'journal');
        
        let message = `Updated to ${keepingTitle} edition`;
        if (bookRemoved) {
          message += '';
        }
        if (journalRemoved) {
          message += ' - Journal updated';
        }
        
        showNotification(message);
      }
      
    } catch (error) {
      console.error('Failed to remove items:', error);
    }
  }
  
  // Show notification
  function showNotification(text) {
    // Remove existing
    const existing = document.querySelector('.variant-notification');
    if (existing) existing.remove();
    
    // Create new
    const notification = document.createElement('div');
    notification.className = 'variant-notification';
    notification.textContent = text;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      max-width: 350px;
      padding: 14px 20px;
      background: #9F0000;
      color: white;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 99999;
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    // Auto remove
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 6000);
  }
  
  // Add animations
  if (!document.querySelector('#variant-exclusivity-styles')) {
    const style = document.createElement('style');
    style.id = 'variant-exclusivity-styles';
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(350px); }
        to { transform: translateX(0); }
      }
      @keyframes slideOut {
        from { transform: translateX(0); }
        to { transform: translateX(350px); }
      }
    `;
    document.head.appendChild(style);
  }
  
  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  document.addEventListener('page:loaded', init);
  
})();

console.log('Variant Exclusivity: Loaded');


// ADD THIS TO THE END OF cart.js
// This will remove ALL orphaned journals when only they remain

(function() {
  'use strict';
  
  // Both journal variant IDs
  const JOURNAL_IDS = [47190821699741, 47190822912157];
  
  // Listen for cart updates
  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function(event) {
    // Skip if no cart data
    if (!event.cart || !event.cart.items || event.cart.items.length === 0) return;
    
    // Check what's in the cart
    let hasRealProducts = false;
    let journalItems = [];
    
    event.cart.items.forEach(item => {
      // Check if it's a journal
      const isJournal = JOURNAL_IDS.includes(item.variant_id) || 
                       JOURNAL_IDS.includes(item.id) ||
                       (item.product_title && item.product_title.includes('Journal'));
      
      if (isJournal) {
        journalItems.push({
          key: item.key,
          id: item.variant_id || item.id
        });
      } else {
        hasRealProducts = true;
      }
    });
    
    // If ONLY journals remain (no real products), remove them ALL
    if (!hasRealProducts && journalItems.length > 0) {
      console.log('Only journals remain, removing all:', journalItems);
      
      // Build updates to remove ALL journals
      const updates = {};
      journalItems.forEach(journal => {
        updates[journal.key] = 0;
      });
      
      // Also clear gift wrap attributes
      const body = JSON.stringify({
        updates: updates,
        attributes: {
          'Gift wrap': '',
          'Gift note': ''
        }
      });
      
      // Remove them
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body
      })
      .then(response => response.json())
      .then(cart => {
        console.log('Orphaned journals removed');
        // Refresh cart display
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: false }
        }));
      })
      .catch(error => {
        console.error('Failed to remove orphaned journals:', error);
      });
    }
  });
  
  console.log('Journal orphan remover: Active');
})();